
import React, { useState, useEffect } from "react";
import { Formik, Form, FieldArray } from "formik";
import {
  Button, Container, Row,
  Col,
} from "reactstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import DropdownInput from "../../common/DropdownInput";
import NameInput from "../../common/NameInput";
import BreadCrumb from "../../Components/Common/BreadCrumb";

import {
  createQuestion,
  updateQuestion,
  getCategoryWithFilter,
  getQuestionWithId,
} from "../../slices/thunks";

const QuestionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const questionId = location.state?.id;

  const [categories, setCategories] = useState([]);
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catRes = await dispatch(getCategoryWithFilter()).unwrap();
        const categoryList = Array.isArray(catRes?.data)
          ? catRes.data.filter((cat) => cat.status === true)
          : [];
        setCategories(categoryList);

        let questionData = null;

        if (questionId) {
          const res = await dispatch(getQuestionWithId(questionId)).unwrap();
          const data = res?.data;

          if (data) {
            const isMCQ = data.type === "mcq";

            questionData = {
              category_id: data.category_id,
              question: data.text,
              question_type: isMCQ ? "MCQ" : "Text",
              correct_answer: data.correct_answer || "",
              options: isMCQ
                ? Object.values(
                  typeof data.options === "string"
                    ? JSON.parse(data.options)
                    : data.options
                )
                : [],
            };
          }
        }

        setInitialValues(
          questionData || {
            category_id: categoryList[0]?.id || "",
            question: "",
            question_type: "MCQ",
            options: [""],
            correct_answer: "",
          }
        );
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [dispatch, questionId]);

  const handleSubmit = async (values, { resetForm }) => {
    const isMCQ = values.question_type === "MCQ";
    const optionsArray = values.options?.filter((opt) => opt.trim() !== "") || [];

    const optionLabels = Array.from({ length: 26 }, (_, i) =>
      String.fromCharCode(97 + i)
    );
    const labeledOptions = {};

    optionsArray.forEach((opt, i) => {
      labeledOptions[optionLabels[i]] = opt;
    });

    const payload = {
      category_id: values.category_id,
      text: values.question,
      type: values.question_type.toLowerCase(),
      options: isMCQ ? labeledOptions : null,
      correct_answer: values.correct_answer,
    };

    try {
      if (questionId) {
        await dispatch(updateQuestion({ id: questionId, payload })).unwrap();
      } else {
        await dispatch(createQuestion(payload)).unwrap();
        resetForm();
      }
      navigate("/quize-question");
    } catch (error) {
      console.error("Submission error:", error);
      alert(`Submission failed: ${error?.message || "Unknown error"}`);
    }
  };

  const handleQuestionTypeChange = async (formik, selectedOption) => {
    const newType = selectedOption?.value || "MCQ";
    formik.setFieldValue("question_type", newType);

    if (newType === "Text") {
      formik.setFieldValue("options", []);
    } else if (newType === "MCQ") {
      if (questionId) {
        try {
          const res = await dispatch(getQuestionWithId(questionId)).unwrap();
          const data = res?.data;
          if (data?.type === "mcq" && data.options) {
            const parsedOptions =
              typeof data.options === "string"
                ? JSON.parse(data.options)
                : data.options;

            const optionsArray = Object.values(parsedOptions);
            formik.setFieldValue("options", optionsArray);
          } else {
            formik.setFieldValue("options", [""]);
          }
        } catch (err) {
          console.error("Error fetching MCQ data:", err);
          formik.setFieldValue("options", [""]);
        }
      } else {
        if (!formik.values.options || formik.values.options.length === 0) {
          formik.setFieldValue("options", [""]);
        }
      }

      if (!formik.values.correct_answer) {
        formik.setFieldValue("correct_answer", "");
      }
    }

    formik.setTouched({});
    formik.setErrors({});
  };

  if (!initialValues) {
    return <div className="text-center p-5">Loading form...</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={questionId ? "Edit Question" : "Add Question"}
          pageTitle="Questions"
        />
      </Container>

      <div className="page-content justify-content-center align-items-center">
        <Container>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form className="p-4 mb-5 border rounded shadow-sm bg-white">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <DropdownInput
                      label="Select Category"

                      name="category_id"
                      id="categoryInput"
                      defaultSelectedKey={formik.values.category_id}
                      onChange={(selectedOption) => {
                        formik.setFieldValue("category_id", selectedOption?.value || "");
                      }}
                      required
                      layout="below"
                      options={categories.map((cat) => ({
                        label: cat.name,
                        value: cat.id,
                      }))}
                      showHelp
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <NameInput
                      label="Question Text"
                      name="question"
                      id="questionInput"
                      value={formik.values.question}
                      onChange={formik.handleChange}
                      required
                      layout="below"
                      showHelp
                      placeholder='What is 2 + 2?'
                      helpText="Enter the question text here (e.g., 'What is 2 + 2?')"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <DropdownInput
                      label="Question Type"

                      name="question_type"
                      id="questionTypeInput"
                      defaultSelectedKey={formik.values.question_type}
                      onChange={(selectedOption) => handleQuestionTypeChange(formik, selectedOption)}
                      required
                      layout="below"
                      options={[
                        { label: "MCQ", value: "MCQ" },
                        { label: "Text", value: "Text" },
                      ]}
                      showHelp
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <NameInput
                      label="Correct Answer"
                      // type="text"
                      id="correct_answer"
                      name="correct_answer"
                      className="form-control"
                      value={formik.values.correct_answer}
                      onChange={formik.handleChange}
                      placeholder={
                        formik.values.question_type === "MCQ"
                          ? "Only one character like a b c d"
                          : "Enter the correct text answer"
                      }
                      helpText={
                        formik.values.question_type === "MCQ"
                          ? "Only one character like a b c d"
                          : "Enter the correct text answer"}
                      maxLength={formik.values.question_type === "MCQ" ? 1 : 200}
                      required
                      showHelp
                    />
                  </div>
                </div>

                {formik.values.question_type === "MCQ" && Array.isArray(formik.values.options) && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Options</label>
                    <FieldArray name="options">
                      {(arrayHelpers) => (
                        <div>
                          {formik.values.options.map((opt, index) => {
                            const label = String.fromCharCode(97 + index);
                            return (
                              <div key={index} className="mb-2 d-flex gap-2 align-items-center">
                                <span className="fw-bold">{label}.</span>
                                <input
                                  className="form-control w-100"
                                  name={`options[${index}]`}
                                  value={opt || ""}
                                  onChange={formik.handleChange}
                                  placeholder="Enter option text"
                                />
                                <Button
                                  type="button"
                                  color="light"
                                  className="fs-13 nav-link"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <i
                                    className="ri-delete-bin-7-fill fs-16 p-2 bg-soft-danger rounded-circle align-middle icon-red"
                                  ></i>   </Button>
                              </div>
                            );
                          })}
                          <Button
                            type="button"
                            color="primary"
                            onClick={() => arrayHelpers.push("")}
                          >
                            Add Option
                          </Button>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                )}
                <Row className="mt-4">
                  <Col lg={12} className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-md"
                      disabled={formik.isSubmitting}
                    >
                      {questionId ? "Update" : "Add"} Question
                     </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Container>
      </div>
    </div>
  );
};

export default QuestionForm;
